import { ApiError, type ApiErrorBody } from '@floorfile/sdk'

export const STALE_UPDATE_EVENT = 'floorfile:stale-update'

export interface StaleUpdateEventDetail {
  message: string
  current?: unknown
}

/**
 * Preserve the shared SDK error envelope even for feature hooks that need the
 * raw-response transport (streaming and presigned-object callers still own
 * their response parsing). Stale edits also notify the one app-level recovery
 * surface, so every editable module gets identical behavior.
 *
 * ## An unreadable success body THROWS; it never becomes `undefined` data
 *
 * This was `response.json().catch(() => undefined)` followed by
 * `return body as T`, so a 2xx whose body could not be read resolved to
 * `undefined` — and React Query rejects an `undefined` query result with
 * "Query data cannot be undefined", logged via `console.error`. The 2026-09-03
 * promote failed exactly there: `['comments','inbox']`,
 * `['notifications','unread']` and `['parts','reorder-requests']` each logged
 * it, and the mobile spine's console-cleanliness assertion caught it.
 *
 * Substituting `undefined` for a failed read is the very class this shared
 * transport exists to prevent: it turns "this request did not work" into
 * "there is nothing here", which renders as an empty badge rather than an
 * error. The per-feature transports this replaced threw here — their bare
 * `res.json()` rejected — which is why the defect appeared only after the
 * 2026-08-15 sweep routed every feature through this function.
 *
 * **204 is the ONLY bodyless success the API emits** — verified by enumeration:
 * every `c.body(null, …)` in `apps/api` is a 204. It is therefore handled
 * before any read is attempted, and every other 2xx is required to carry an
 * envelope. If that ever stops being true, this throws loudly rather than
 * inventing an empty result, which is the side to fail on.
 */
export async function parseApiResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T

  let body: (ApiErrorBody & { data?: T }) | undefined
  let readError: unknown
  let unreadable = false
  try {
    body = (await response.json()) as ApiErrorBody & { data?: T }
  } catch (error) {
    unreadable = true
    readError = error
  }

  if (!response.ok) {
    // FF-AUDIT-20260914-210459-096: a 5xx with NO FloorFile envelope is not
    // the API speaking — it is the service worker's dead-zone answer, a proxy
    // or a gateway in front of it. Name that kind once, here, so every screen
    // can say "can't reach the server" instead of "does not exist" or the
    // transport's own words. A real envelope (DB_UNAVAILABLE, NOT_FOUND) keeps
    // its meaning.
    const unreachable = response.status >= 500 && !body?.error
    const error = new ApiError(response.status, body?.error ?? {
      code: unreachable ? 'UNREACHABLE' : 'INTERNAL',
      message: unreachable ? 'The server could not be reached.' : `Request failed (${response.status})`,
    })
    if (error.code === 'STALE_UPDATE' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<StaleUpdateEventDetail>(STALE_UPDATE_EVENT, {
        detail: {
          message: error.message,
          ...(error.details?.[0] !== undefined ? { current: error.details[0] } : {}),
        },
      }))
    }
    throw error
  }

  /**
   * Rethrow the ACTUAL read failure, unchanged.
   *
   * ⚠ The dominant cause here is a **cancellation, not a server fault**, and
   * this was measured rather than assumed (2026-09-03, in the running app):
   * when a navigation or an unmount aborts an in-flight request after its
   * headers have arrived, the response still reports `ok: true, status: 200`
   * and `.json()` rejects with an `AbortError`.
   *
   * That is precisely what blocked the promote. `mobile-overflow.spec.ts`
   * walks ~30 routes with one console watcher over the whole test, so the
   * shell-level queries that fire on EVERY route — `['comments','inbox']`,
   * `['notifications','unread']`, `['parts','reorder-requests']` — are the
   * ones most often still in flight when the next `goto()` lands.
   *
   * So do NOT synthesise `ApiError(200, INTERNAL)` here: an aborted request is
   * not a failed one, React Query keys its cancellation handling on the
   * `AbortError`, and a user who simply navigated away must not be shown an
   * error. Rethrowing the original also keeps a genuine `SyntaxError` (a truly
   * malformed body) honest instead of relabelling it.
   *
   * What must never happen again is returning `undefined`: React Query rejects
   * an undefined result with "Query data cannot be undefined" on `console.error`
   * — a real console error for a non-event, and an empty badge where the reader
   * should see nothing at all.
   */
  if (unreadable) {
    throw readError
  }

  return body as T
}

/** Send both supported concurrency tokens during the compatibility window. */
export function versionedPatch(version: number, input: Record<string, unknown>): RequestInit {
  return {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', 'If-Match': String(version) },
    body: JSON.stringify({ ...input, expectedVersion: version }),
  }
}

/**
 * FF-AUDIT-20260914-210459-096: is this failure "the server could not be
 * reached" — the transport's own `TypeError` (`Failed to fetch`, `NetworkError
 * when attempting to fetch resource`, Safari's `Load failed`) or the
 * `UNREACHABLE` envelope this module minted for a bodiless 5xx? Screens key
 * their wording on this, never on the message text.
 */
export function isUnreachableError(error: unknown): boolean {
  if (error instanceof TypeError) return true
  const code = (error as { code?: unknown } | null)?.code
  return code === 'UNREACHABLE'
}
